import * as H from "history";
import React, { Component, Fragment } from "react";
import { connect } from "react-redux";
import _, { Dictionary } from "lodash";
import { IRootState } from "../../../../store";
import DispatchProps from "../../../../utils/DispatchProps";
import ApiService from "../../../ApiService";
import Table, { TableColumn } from "../../../Table";
import { TableRowProps } from "../../../TableRow";
import Actions from "../../../../redux/Actions";
import {Dropdown, IDropdownOption, MessageBarType} from "office-ui-fabric-react";
import TableCell from "../../../TableCell";
import { MailroomAutomationHistoryRecord } from "../../../../models/MailroomAutomationHistoryRecord";
import TableBoxDataView, { TableBoxDataViewItem } from "../../../TableBoxDataView";
import moment from "moment";

import {getDateRange} from "../../../../utils/Utils";
import {Bar} from "react-chartjs-2";
import {BAR_CHART_OPTIONS} from "../../../AssignmentsOverTimeCard";

type TableBoxOption = "status" | "sdsCsvChanged" | "processorItemsChanged" | "processorItemsChangedPercentage";
const TABLE_BOX_DROPDOWN_OPTIONS: IDropdownOption[] = [
    {
        text: "status",
        key: "status"
    },
    {
        text: "sdsCsvChanged",
        key: "sdsCsvChanged"
    },
    {
        text: "processorItemsChanged",
        key: "processorItemsChanged"
    },
    {
        text: "processorItemsChangedPercentage",
        key: "processorItemsChangedPercentage"
    }
]

interface MailroomAutomationsPageProps {
    apiService: ApiService;
    history: H.History;
}

interface MailroomAutomationsPageState {
    loading: boolean;

    selectedTableBoxOption: TableBoxOption;
    automationRecords: Dictionary<MailroomAutomationHistoryRecord[]>;
    lastAutomationForEachFlowPerDay: Dictionary<MailroomAutomationHistoryRecord[]>;
    daysToLoad: number;
    startDate: string;
    endDate: string;
}

class MailroomAutomationsPage extends Component<MailroomAutomationsPageProps & DispatchProps, MailroomAutomationsPageState> {

    constructor(props: MailroomAutomationsPageProps & DispatchProps) {
        super(props);

        const daysToLoad = 30;

        this.state = {
            loading: false,
            selectedTableBoxOption: "status",
            automationRecords: {},
            lastAutomationForEachFlowPerDay: {},
            daysToLoad: daysToLoad,
            endDate: moment.utc().format(),
            startDate: moment.utc().add(-daysToLoad, 'days').format()
        };
    }

    async componentDidMount() {
        await this.load();
    }

    private async load() {
        try {
            this.setState({ loading: true, automationRecords: {} });

            const allRecords = await this.props.apiService.getAllMailroomAutomationHistoryRecords(this.state.daysToLoad.toString());

            const groupedRecords = _.groupBy(allRecords, r => `${r.tenantId}-${r.profileId}`);
            const lastAutomationForEachFlowPerDay = this.calcLastAutomationForEachFlowPerDay(this.state.startDate,this.state.endDate,groupedRecords);
            
            this.setState({
                loading: false,
                automationRecords: groupedRecords,
                lastAutomationForEachFlowPerDay: lastAutomationForEachFlowPerDay
            });
        }
        catch (e) {
            this.setState({ loading: false, automationRecords: {}, lastAutomationForEachFlowPerDay: {} });
            console.log(e);
            this.props.dispatch(Actions.showMessageBar(`Error loading automation statistics status by flow`, MessageBarType.error));
        }
    }

    private toStatusTableBoxItem = (item: MailroomAutomationHistoryRecord): TableBoxDataViewItem => {
        let backgroundColour = "#95cf95";
        let title = `${moment(item.dateTime).format("YYYY-MM-DD HH:mm")}`;

        if (item.message !== null) {
            title += `\nMessage: ${item.message}`;
        }

        if (item.error) {
            backgroundColour = "#df7373";
        }

        if (item.stats.processorTotalChangeCount !== null) {
            title += `\nChange Count: ${item.stats.processorTotalChangeCount}`;
        }

        return {
            date: item.dateTime,
            title: title,
            backgroundColour: backgroundColour,
        }
    }
    
    private toProcessorItemsChangedTableBoxItem = (item: MailroomAutomationHistoryRecord): TableBoxDataViewItem => {
        let title = `${moment(item.dateTime).format("YYYY-MM-DD HH:mm")}`;

        let backgroundColour = undefined;
        if(item.stats.processorTotalChangeCount !== null)
        {
            backgroundColour = item.stats.processorTotalChangeCount > 0 ? "#e7b416": "#b0aeae";
            
            title += `\n${item.stats.processorTotalChangeCount} total changes`;
        }

        return {
            date: item.dateTime,
            title: title,
            backgroundColour: backgroundColour,
        }
    }

    private toProcessorItemsChangedPercentageTableBoxItem = (item: MailroomAutomationHistoryRecord): TableBoxDataViewItem => {
        let title = `percent ${moment(item.dateTime).format("YYYY-MM-DD HH:mm")}`;

        let backgroundColour = undefined;
        if(item.stats.processorTotalChangeCount !== null && item.stats.processorTotalItemCount !== null)
        {
            if(item.stats.processorTotalChangeCount === 0)
            {
                backgroundColour = "b0aeae";
            }
            else
            {
                let percentage = (item.stats.processorTotalChangeCount / item.stats.processorTotalItemCount) * 100;
                percentage = Math.max(percentage,2);
                backgroundColour = `linear-gradient(0deg, #e7b416 ${percentage}%, #b0aeae ${percentage}%)`
            }

            title += `\n${item.stats.processorTotalChangeCount} total changes`;
        }

        return {
            date: item.dateTime,
            title: title,
            backgroundColour: backgroundColour,
        }
    }

    private toSdsCsvChangedTableBoxItem = (item: MailroomAutomationHistoryRecord): TableBoxDataViewItem => {
        
        let title = `${moment(item.dateTime).format("YYYY-MM-DD HH:mm")}`;
        
        const backgroundColour = item.stats.uploadSnapshotMatchesPrevious === false ? "#e7b416": "#b0aeae";
        
        return {
            date: item.dateTime,
            title: title,
            backgroundColour: backgroundColour,
        }
    }


    private toTableBoxDataViewItem =  (item: MailroomAutomationHistoryRecord): TableBoxDataViewItem => {
        switch (this.state.selectedTableBoxOption)
        {
            case "status":
                return this.toStatusTableBoxItem(item);

            case "sdsCsvChanged":
                return this.toSdsCsvChangedTableBoxItem(item);
                
            case "processorItemsChanged":
                return this.toProcessorItemsChangedTableBoxItem(item);
            case "processorItemsChangedPercentage":
                return this.toProcessorItemsChangedPercentageTableBoxItem(item);
        }
        
    }

    private columns: TableColumn[] = [
        {
            title: "ProfileId/TenantId",
            propertyPath: "Key",
            flex: 1,
        },
        {
            title: `Automation Runs`,
            propertyPath: "AutomationRuns",
            flex: 5,
        }
    ];

    private rowProps = (row: any): TableRowProps => {
        return {
            enableRowLink: false
        };
    }

    private buildRow = (row: MailroomAutomationHistoryRecord[], column: TableColumn, index: number): JSX.Element | undefined => {
        switch (column.propertyPath) {
            case "Key":
                return <TableCell key={`${index}-${column.propertyPath}`} flex={column.flex}>
                    <span>{row[0].profileId}</span>
                    <span className={"cdb-table__extra-detail"}>{row[0].tenantId}</span>
                </TableCell>;

            case "AutomationRuns":
                return <TableCell key={`${index}-${column.propertyPath}`} flex={column.flex}>
                    <TableBoxDataView
                        startDate={this.state.startDate}
                        endDate={this.state.endDate}
                        data={row.map(m => this.toTableBoxDataViewItem(m))} />
                </TableCell>;

            default:
                return undefined;
        }
    }
    
    private calcLastAutomationForEachFlowPerDay(startDate: string,endDate: string,groupedAutomationRecords: Dictionary<MailroomAutomationHistoryRecord[]>)
    {
        const dateRange = getDateRange(startDate,endDate);

        const lastAutomationForEachFlowPerDay: Dictionary<MailroomAutomationHistoryRecord[]> = {};
        
        dateRange.forEach(date => {
            lastAutomationForEachFlowPerDay[date.startOf('day').toISOString()] = Object.keys(groupedAutomationRecords)
                .map(automationId => {
                    const historyForAutomationForDay = groupedAutomationRecords[automationId].filter(hr => moment(hr.dateTime).isSame(date,'day'))
                    historyForAutomationForDay.sort((a,b) => moment(b.dateTime).diff(moment(a.dateTime)));
                    return historyForAutomationForDay[0];
            }).filter(v => v !== undefined);
        })
        
        return lastAutomationForEachFlowPerDay;
    }
    
    private buildSummaryCard()
    {
        let barChartData: any = {};
        let barChartTitle: string = "";
        let barChartExtraDetail: string = "If multiple automation runs in a day, only the last run is analysed."
        
        const buildCountBarChartDataset = (lastAutomationForEachFlowPerDay: Dictionary<MailroomAutomationHistoryRecord[]>,condition: (mhr: MailroomAutomationHistoryRecord) => boolean,label: string,colour: string): any => {
            const data = Object.keys(this.state.lastAutomationForEachFlowPerDay).map(dateString => {
                
                return {
                    t: moment(dateString).toDate(),
                    y: this.state.lastAutomationForEachFlowPerDay[dateString].filter(condition).length
                }
            });
            
            return {
                label: label,
                backgroundColor: colour,
                data: data
            };
        };

        const buildSumBarChartDataset = (lastAutomationForEachFlowPerDay: Dictionary<MailroomAutomationHistoryRecord[]>,value: (mhr: MailroomAutomationHistoryRecord) => number,label: string,colour: string): any => {
            const data = Object.keys(this.state.lastAutomationForEachFlowPerDay).map(dateString => {

                return {
                    t: moment(dateString).toDate(),
                    y: _.sumBy(this.state.lastAutomationForEachFlowPerDay[dateString],value)
                }
            });

            return {
                label: label,
                backgroundColor: colour,
                data: data
            };
        };

        switch (this.state.selectedTableBoxOption)
        {
            case "status":
                barChartData = {
                    datasets: [
                        buildCountBarChartDataset(this.state.lastAutomationForEachFlowPerDay,(r) => r?.error === false,"Completed","#99c140"),
                        buildCountBarChartDataset(this.state.lastAutomationForEachFlowPerDay,(r) => r?.error === true,"Failed","#cc3232")
                    ]
                }
                barChartTitle = "Count of automation status";
                break;
                
            case "processorItemsChanged":
                barChartData = {
                    datasets: [
                        buildCountBarChartDataset(this.state.lastAutomationForEachFlowPerDay,(r) => r?.stats.processorTotalChangeCount !== null && r?.stats.processorTotalChangeCount > 0,"Changes","#e7b416"),
                        buildCountBarChartDataset(this.state.lastAutomationForEachFlowPerDay,(r) => r?.stats.processorTotalChangeCount === null || r?.stats.processorTotalChangeCount === 0,"No Changes","#b0aeae"),
                    ],
                }
                barChartTitle = "Count of automations with processor item changes";
                break;
                
            case "sdsCsvChanged":
                barChartData = {
                    datasets: [
                        buildCountBarChartDataset(this.state.lastAutomationForEachFlowPerDay,(r) => r?.stats.uploadSnapshotMatchesPrevious === false,"CSVs Changed","#e7b416"),
                        buildCountBarChartDataset(this.state.lastAutomationForEachFlowPerDay,(r) => r?.stats.uploadSnapshotMatchesPrevious === true,"CSVs Not Changed Changes","#b0aeae"),
                    ],
                }
                barChartTitle = "Count of automations which uploaded changed CSVs to SDS";
                break;
            case "processorItemsChangedPercentage":
                barChartData = {
                    datasets: [
                        buildSumBarChartDataset(this.state.lastAutomationForEachFlowPerDay,(r) => r.stats.processorTotalChangeCount!,"Total Changed","#e7b416"),
                        buildSumBarChartDataset(this.state.lastAutomationForEachFlowPerDay,(r) => r.stats.processorTotalItemCount! - r.stats.processorTotalChangeCount!,"Total Unchanged","#b0aeae"),
                    ]
                }
                barChartTitle = "Sum of processor item changes";
                break;
        }
        
        return (
            <div className={"card card--padded flex-item--flex-1"}>
                <Bar data={barChartData} width={8} height={1} options={BAR_CHART_OPTIONS}/>
                <span className={"p-top-1rem"}>{barChartTitle}</span>
                <span className={"text-extra-detail"}>{barChartExtraDetail}</span>
            </div>
        )
    }

    render() {
        return (
            <Fragment>
            {this.buildSummaryCard()}
                <Table title="Mailroom Automations"
                       items={_.toArray(this.state.automationRecords)}
                       columns={this.columns}
                       cellForColumn={this.buildRow}
                    rowPropsForItem={this.rowProps}
                    loading={this.state.loading}
                    enableSort={true}>

                    <Dropdown
                        className={"p-right-05rem"}
                        selectedKey={this.state.selectedTableBoxOption}
                        onChange={(e,o) => {
                            this.setState({
                                selectedTableBoxOption: o?.key as TableBoxOption
                            });
                        }}
                        options={TABLE_BOX_DROPDOWN_OPTIONS}
                        styles={{ dropdown: { width: 300 } }}
                    />
                </Table>
            </Fragment>
        );
    }
}

export default connect(
    (state: IRootState, ownProps: { history: H.History, match: any }): MailroomAutomationsPageProps => {
        return {
            apiService: state.auth.apiService,
            history: ownProps.history
        };
    }
)(MailroomAutomationsPage);